import { createRoot, type Root } from 'react-dom/client';
import type Canvas from './Canvas';
import styles from './ExternalContent.module.css';

// External content are the React components rendered outside the Canvas
// by some whiteboard objects which have HTML representations.
class ExternalContent {
  _contentContainers = new Map<
    fabric.Object,
    { el: HTMLDivElement; root: Root }
  >();

  constructor(public container: HTMLDivElement, public canvas: Canvas) {
    this.container.classList.add(styles.externalContent);

    this.container.addEventListener('mousedown', () => this.blur());
    canvas.on('pan', () => this.positionContainers());
    canvas.on('zoom', () => this.positionContainers());
  }

  add(object: fabric.Object) {
    if (!this._contentContainers.has(object)) {
      const container = document.createElement('div');
      container.classList.add(styles.container);
      const root = createRoot(container);
      this._contentContainers.set(object, { el: container, root });
      this.container.appendChild(container);

      this.render(object);
    }
  }

  remove(object: fabric.Object) {
    const container = this._contentContainers.get(object);
    if (!container) return;

    container.root.unmount();
    container.el.remove();
    this._contentContainers.delete(object);
  }

  render(object: fabric.Object) {
    const container = this._contentContainers.get(object);
    if (!container) return;

    this.positionContainer(object);

    // Render the react component into the container.
    container.root.render(object.renderExternalContent());
  }

  destroy() {
    Array.from(this._contentContainers.keys()).forEach(object =>
      this.remove(object)
    );
    this.container.remove();
  }

  positionContainers() {
    this._contentContainers.forEach((_, object) =>
      this.positionContainer(object)
    );
  }

  positionContainer(object: fabric.Object) {
    let { scaleX, scaleY, left, top, width, height, angle, z } = object;
    const zoom = this.canvas.getZoom();
    width *= scaleX / zoom;
    height *= scaleY / zoom;

    const coords = this.canvas.rectToWindowCoords({ left, top, width, height });
    const container = this._contentContainers.get(object);
    if (container) {
      Object.assign(container.el.style, {
        position: 'absolute',
        transform: `scale(${zoom}) rotate(${angle}deg)`,
        transformOrigin: 'top left',
        overflow: 'hidden',
        zIndex: z,
        left: `${coords.left}px`,
        top: `${coords.top}px`,
        width: `${coords.width}px`,
        height: `${coords.height}px`,
      });
    }
  }

  focus(object: fabric.Object) {
    this.canvas.containerElement.style.pointerEvents = 'none';
    this.container.classList.add(styles.externalContentFocusing);

    const focusedContainer = this._contentContainers.get(object);
    this._contentContainers.forEach(container => {
      const focused = container === focusedContainer;

      container.el.classList.toggle(styles.focusedContainer, focused);
      container.el.classList.toggle(styles.disabledContainer, !focused);
    });
  }

  blur() {
    this.canvas.containerElement.style.pointerEvents = 'all';
    this.container.classList.remove(styles.externalContentFocusing);

    this._contentContainers.forEach(container => {
      container.el.classList.remove(styles.focusedContainer);
      container.el.classList.remove(styles.disabledContainer);
    });

    this.canvas.controller.closeAhaDrawer();

    // Notify controller that interaction has ended
    this.canvas.controller.actionShowExternalContentExitButton(false);
  }
}

export default ExternalContent;
